import React, { useCallback, useMemo, useState, lazy, memo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
// =============================================
//
//  📘 CARRUSEL DE PÁGINAS — CÓMO USAR
//
//  1. Subí tu imagen a Assets (o usá una URL externa)
//  2. Copiá la URL de la imagen
//  3. Pegala en el array SLIDE_URLS (una por línea)
//  4. Para quitar una imagen, borrá la línea o dejala vacía ''
//
//  Ejemplo:
//  const SLIDE_URLS: string[] = [
//    'https://cdn.shopify.com/s/files/.../mi-pagina-1.png',
//    'https://cdn.shopify.com/s/files/.../mi-pagina-2.png',
//  ]
//
//  Máximo recomendado: 10 imágenes.
//  Formato ideal: A4 vertical (proporción ~1:1.414)
//
//  Este componente es portable: copiá este archivo
//  a otro proyecto React + Tailwind y funciona solo.
//  Única dependencia de íconos: lucide-react (ChevronLeft/Right)
//
// =============================================
const SLIDE_URLS: string[] = ['https://cdn.shopify.com/s/files/1/0722/4629/6767/files/Imagen_01_png.png?v=1770600244', 'https://cdn.shopify.com/s/files/1/0722/4629/6767/files/Imagen_02_png.png?v=1770600243', 'https://cdn.shopify.com/s/files/1/0722/4629/6767/files/Imagen_03_png.png?v=1770600244', 'https://cdn.shopify.com/s/files/1/0722/4629/6767/files/Imagen_04_png.png?v=1770600243', 'https://cdn.shopify.com/s/files/1/0722/4629/6767/files/Imagen_05_png.png?v=1770600243', 'https://cdn.shopify.com/s/files/1/0722/4629/6767/files/Imagen_06_png.png?v=1770600244', 'https://cdn.shopify.com/s/files/1/0722/4629/6767/files/Imagen_07_png.png?v=1770600244', 'https://cdn.shopify.com/s/files/1/0722/4629/6767/files/Imagen_08_png.png?v=1770600244', 'https://cdn.shopify.com/s/files/1/0722/4629/6767/files/Imagen_09_png.png?v=1770600244', 'https://cdn.shopify.com/s/files/1/0722/4629/6767/files/Imagen_10_png.png?v=1770600243'];
// ── Props (reutilizable) ──────────────────────
interface PagePreviewCarouselProps {
  urls?: string[];
  title?: string;
  subtitle?: string;
}
export function PagePreviewCarousel({
  urls,
  title = '👉 Revisá algunas páginas del material',
  subtitle = 'Deslizá para ver una muestra real del contenido'
}: PagePreviewCarouselProps = {}) {
  // Filtrar URLs vacías (memoizado para evitar re-cálculos)
  const slides = useMemo(() => (urls ?? SLIDE_URLS).filter(u => u.trim() !== ''), [urls]);
  const [current, setCurrent] = useState(0);
  const [touchX, setTouchX] = useState<number | null>(null);
  const [failedImages, setFailedImages] = useState<Set<number>>(new Set());
  const single = slides.length <= 1;
  const prev = useCallback(() => setCurrent(c => c === 0 ? slides.length - 1 : c - 1), [slides.length]);
  const next = useCallback(() => setCurrent(c => c === slides.length - 1 ? 0 : c + 1), [slides.length]);
  const handleImageError = useCallback((index: number) => {
    setFailedImages(prev => new Set(prev).add(index));
  }, []);
  // Nada que mostrar
  if (slides.length === 0) return null;
  return <section className="py-14 px-4 bg-white" data-id="element-203">
      <div className="max-w-3xl mx-auto" data-id="element-204">
        {/* Título y subtítulo */}
        <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-2" data-id="element-205">
          {title}
        </h2>
        <p className="text-center text-gray-500 mb-8" data-id="element-206">{subtitle}</p>

        <div className="relative" data-id="element-207">
          {/* ── Viewport ── */}
          <div className="overflow-hidden rounded-xl" onTouchStart={e => setTouchX(e.touches[0].clientX)} onTouchMove={e => e.preventDefault} onTouchEnd={e => {
          if (touchX === null || single) return;
          const diff = touchX - e.changedTouches[0].clientX;
          if (diff > 50) next();
          if (diff < -50) prev();
          setTouchX(null);
        }} data-id="element-208">
            <div className="flex transition-transform duration-300 ease-out" style={{
            transform: `translateX(-${current * 100}%)`
          }} data-id="element-209">
              {slides.map((url, i) => <div key={i} className="min-w-full flex justify-center items-center px-3 md:px-8" data-id="element-210">
                  {failedImages.has(i) /* ── Placeholder si la imagen falla ── */ ? <div className="w-full max-w-xs md:max-w-sm aspect-[1/1.414] bg-gray-100 rounded-lg border border-gray-200 flex flex-col items-center justify-center text-gray-400 text-sm" data-id="element-211">
                      <svg className="w-10 h-10 mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-212">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z" data-id="element-213" />
                      </svg>
                      No se pudo cargar esta página
                    </div> /* ── Imagen A4 ── */ : <div className="w-full max-w-xs md:max-w-sm" style={{
                maxHeight: '70vh'
              }} data-id="element-214">
                      <div className="aspect-[1/1.414] w-full bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-215">
                        <img src={url} alt={`Página ${i + 1} del material`} className="w-full h-full object-contain" loading={i === 0 ? 'eager' : 'lazy'} decoding="async" {...i === 0 ? {
                    fetchPriority: 'high' as any
                  } : {}} draggable={false} onError={() => handleImageError(i)} data-id="element-216" />
                      </div>
                    </div>}
                </div>)}
            </div>
          </div>

          {/* ── Flechas (desktop visibles, mobile ocultas) ── */}
          {!single && <>
              <button onClick={prev} className="hidden md:flex absolute top-1/2 left-0 -translate-x-14 -translate-y-1/2 w-11 h-11 rounded-full bg-white border border-gray-200 text-gray-500 hover:text-gray-900 hover:bg-gray-50 hover:border-gray-300 shadow-sm items-center justify-center transition-all" aria-label="Página anterior" data-id="element-217">
                <ChevronLeft size={22} data-id="element-218" />
              </button>
              <button onClick={next} className="hidden md:flex absolute top-1/2 right-0 translate-x-14 -translate-y-1/2 w-11 h-11 rounded-full bg-white border border-gray-200 text-gray-500 hover:text-gray-900 hover:bg-gray-50 hover:border-gray-300 shadow-sm items-center justify-center transition-all" aria-label="Página siguiente" data-id="element-219">
                <ChevronRight size={22} data-id="element-220" />
              </button>
            </>}
        </div>

        {/* ── Dots ── */}
        {!single && <div className="flex justify-center mt-6 gap-2.5" data-id="element-221">
            {slides.map((_, i) => <button key={i} onClick={() => setCurrent(i)} className={`rounded-full transition-all duration-200 ${current === i ? 'w-6 h-2.5 bg-gray-800' : 'w-2.5 h-2.5 bg-gray-300 hover:bg-gray-400'}`} aria-label={`Ir a página ${i + 1}`} data-id="element-222" />)}
          </div>}

        {/* ── Contador ── */}
        {!single && <p className="text-center text-gray-400 text-sm mt-3" data-id="element-223">
            {current + 1} / {slides.length}
          </p>}
      </div>
    </section>;
}